import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getInventory } from "../../services/inventoryService";
import { AiOutlineWarning, AiOutlineShoppingCart, AiOutlineEdit } from "react-icons/ai";
import InventoryRegistrationModal from "../../pages/Inventory/InventoryRegistrationModal";

function LowStockItems({ showPopup }) {
    const [lowStockItems, setLowStockItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        fetchLowStockItems();
    }, []);

    const fetchLowStockItems = async () => {
        setIsLoading(true);
        try {
            const response = await getInventory();
            const inventoryItems = response.data || [];

            // Filter items with quantity below or equal to reorder level
            const lowStock = inventoryItems.filter(item =>
                item.quantity <= item.reorder_level
            );

            setLowStockItems(lowStock);
        } catch (error) {
            console.error("Error fetching low stock items:", error);
            showPopup("Failed to load low stock items", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedItem(null);
    };

    const handleInventoryUpdated = () => {
        fetchLowStockItems();
        showPopup("Inventory updated successfully", "success");
    };

    // Calculate the criticality of the stock level
    const getStockPercentage = (quantity, reorderLevel) => {
        if (quantity === 0) return 0;

        // Calculate as a percentage of the reorder level
        const percentage = (quantity / reorderLevel) * 100;
        return Math.min(percentage, 100); // Cap at 100%
    };

    const getStockStatusClass = (quantity, reorderLevel) => {
        const percentage = getStockPercentage(quantity, reorderLevel);

        if (percentage === 0) return "out-of-stock";
        if (percentage <= 50) return "critical";
        return "warning";
    };

    return (
        <Container>
            <Header>
                <TitleWithIcon>
                    <AiOutlineWarning className="warning-icon" />
                    <h2>Low Stock Items</h2>
                </TitleWithIcon>
                <RefreshButton onClick={fetchLowStockItems}>
                    <AiOutlineShoppingCart />
                </RefreshButton>
            </Header>

            {isLoading ? (
                <LoadingState>Loading low stock items...</LoadingState>
            ) : (
                <ItemsList>
                    {lowStockItems.length > 0 ? (
                        lowStockItems.map((item) => (
                            <ItemCard key={item.id}>
                                <ItemDetails>
                                    <ItemName>{item.product_name}</ItemName>
                                    <ItemInfo>
                                        <StockLevel className={getStockStatusClass(item.quantity, item.reorder_level)}>
                                            {item.quantity} in stock
                                        </StockLevel>
                                        <ReorderLevel>Reorder at: {item.reorder_level}</ReorderLevel>
                                    </ItemInfo>
                                </ItemDetails>

                                <StockIndicator>
                                    <ProgressBar>
                                        <Progress
                                            percentage={getStockPercentage(item.quantity, item.reorder_level)}
                                            status={getStockStatusClass(item.quantity, item.reorder_level)}
                                        />
                                    </ProgressBar>
                                </StockIndicator>

                                <ActionButton onClick={() => handleEdit(item)}>
                                    <AiOutlineEdit />
                                    <span>Update</span>
                                </ActionButton>
                            </ItemCard>
                        ))
                    ) : (
                        <EmptyState>
                            <p>No low stock items found</p>
                            <small>All inventory items are above their reorder levels</small>
                        </EmptyState>
                    )}
                </ItemsList>
            )}

            {isModalOpen && (
                <InventoryRegistrationModal
                    onClose={handleModalClose}
                    selectedItem={selectedItem}
                    onInventoryUpdated={handleInventoryUpdated}
                />
            )}
        </Container>
    );
}

export default LowStockItems;

const Container = styled.div`
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h2 {
        font-size: 18px;
        margin: 0;
        color: #333;
    }
    
    .warning-icon {
        color: #ff9800;
        margin-right: 8px;
    }
`;

const TitleWithIcon = styled.div`
    display: flex;
    align-items: center;
`;

const RefreshButton = styled.button`
    background: none;
    border: none;
    color: #777;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #000;
    }
`;

const LoadingState = styled.div`
    text-align: center;
    padding: 30px;
    color: #777;
`;

const ItemsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 500px;
    overflow-y: auto;
`;

const ItemCard = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    border-radius: 6px;
    background-color: #f9f9f9;
    border-left: 3px solid #ff9800;
`;

const ItemDetails = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
`;

const ItemName = styled.div`
    font-weight: 600;
    font-size: 14px;
`;

const ItemInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
`;

const StockLevel = styled.div`
    font-size: 12px;
    font-weight: 500;
    border-radius: 20px;
    padding: 2px 8px;
    
    &.warning {
        background-color: #fff3e0;
        color: #ff9800;
    }
    
    &.critical {
        background-color: #ffebee;
        color: #f44336;
    }
    
    &.out-of-stock {
        background-color: #b71c1c;
        color: white;
    }
`;

const ReorderLevel = styled.div`
    font-size: 11px;
    color: #777;
`;

const StockIndicator = styled.div`
    margin-bottom: 15px;
`;

const ProgressBar = styled.div`
    height: 6px;
    background-color: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;
`;

const Progress = styled.div`
    height: 100%;
    width: ${props => props.percentage}%;
    background-color: ${props => {
    if (props.status === 'out-of-stock') return '#b71c1c';
    if (props.status === 'critical') return '#f44336';
    return '#ff9800';
}};
    transition: width 0.3s ease;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background-color: #000;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px;
    cursor: pointer;
    font-size: 13px;
    transition: background-color 0.2s;
    
    &:hover {
        background-color: #333;
    }
    
    svg {
        font-size: 14px;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 30px 15px;
    color: #777;
    
    p {
        font-size: 16px;
        margin-bottom: 5px;
    }
    
    small {
        font-size: 13px;
        color: #999;
    }
`;